import { motion, AnimatePresence } from "framer-motion";
import React, { ChangeEvent, FormEvent, useState } from "react";

import { registerV2API, verifyRegisterOtpV2API } from "../../api/authApiService";
import { showToast } from "../Toast";
import { useLoginSession } from "../../logic/useLoginSession";
import { RegisterFormData } from "../../types/auth.types";
import { __fnGetFormatedCurrentDate } from "../../utilities/Common";

const MIN_PASSWORD_LENGTH = 6;

interface RegisterFormProps {
  onSwitchToLogin: () => void;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onSwitchToLogin }) => {
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [form, setForm] = useState<RegisterFormData>({
    name: "",
    emailId: "",
    mobile: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [isRegistered, setIsRegistered] = useState(false);
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [otpToken, setOtpToken] = useState<string>("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const { signIn } = useLoginSession();

  // Auto-focus first OTP input when OTP screen appears
  React.useEffect(() => {
    if (isRegistered) {
      const firstInput = document.getElementById("register-otp-0");
      firstInput?.focus();
    }
  }, [isRegistered]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleOtpChange = (value: string, index: number) => {
    if (/^\d?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < 5) {
        const nextInput = document.getElementById(`register-otp-${index + 1}`);
        nextInput?.focus();
      }

      if (newOtp.every((digit) => digit !== "")) {
        const verifyBtn = document.getElementById("register-verify-btn");
        verifyBtn?.focus();
      }
    }
  };

  const handleOtpKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.key === "Backspace") {
      if (otp[index]) {
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        const newOtp = [...otp];
        newOtp[index - 1] = "";
        setOtp(newOtp);
        const prevInput = document.getElementById(`register-otp-${index - 1}`);
        prevInput?.focus();
      }
    }
  };

  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!form.name.trim()) newErrors.name = "Name is required.";
    if (!form.emailId.trim()) newErrors.emailId = "Email is required.";
    else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(form.emailId))
      newErrors.emailId = "Invalid email address";
    if (!form.mobile.trim()) newErrors.mobile = "Mobile number is required.";
    else if (!/^\d{10}$/.test(form.mobile))
      newErrors.mobile = "Mobile number must be 10 digits.";
    if (!form.password) newErrors.password = "Password is required.";
    else if (form.password.length < MIN_PASSWORD_LENGTH)
      newErrors.password = `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
    if (!form.confirmPassword)
      newErrors.confirmPassword = "Please confirm your password.";
    else if (form.confirmPassword !== form.password)
      newErrors.confirmPassword = "Passwords do not match.";
    return newErrors;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setIsSaving(true);

    try {
      const response = await registerV2API({
        name: form.name,
        emailId: form.emailId,
        mobile: form.mobile,
        postingDate: __fnGetFormatedCurrentDate(),
        password: form.password,
        confirmPassword: form.confirmPassword,
      });

      const otpSent =
        response?.status === 200 &&
        (response.messageCode === 100 || response.messageCode === 108);

      if (otpSent) {
        showToast({
          type: response.messageCode === 108 ? "success" : "warning",
          message: response.message || "We've sent a code to your email.",
          timer: response.messageCode === 108 ? 1500 : 3000,
        });
        setOtpToken(response.data?.token || "");
        setOtp(Array(6).fill(""));
        setIsRegistered(true);
      } else {
        showToast({
          type: "error",
          message: response?.message || "Registration failed. Please try again.",
          timer: 3000,
        });
      }
    } catch (error: any) {
      showToast({
        type: "error",
        message: error.response?.data?.message || "Failed to save customer.",
        timer: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const resetOtpInputs = () => {
    setOtp(Array(6).fill(""));
    document.getElementById("register-otp-0")?.focus();
  };

  const handleVerifyOtp = async () => {
    const otpCode = otp.join("");
    if (otpCode.length < 6) {
      showToast({ type: "error", message: "Please enter all 6 digits", timer: 3000 });
      return;
    }
    setIsVerifying(true);
    try {
      const response = await verifyRegisterOtpV2API({
        emailId: form.emailId,
        token: otpToken,
        otpCode,
        postingDate: __fnGetFormatedCurrentDate(),
      });

      if (response?.messageCode === 100) {
        // Verified — sign straight in with the password they just chose. This
        // also finishes any wishlist/cart/Buy Now click that sent them here
        // and closes this modal.
        setIsSigningIn(true);
        const result = await signIn(
          { username: form.emailId, password: form.password },
          "Account created — you're logged in!",
        );

        if (!result.ok) {
          // The account exists; only the automatic login failed. A saved
          // product click is kept and runs after a manual login.
          showToast({
            type: "warning",
            message: "Your account is ready. Please log in to continue.",
            timer: 3000,
          });
          onSwitchToLogin();
        }
        return;
      }

      if (response?.messageCode === 102) {
        showToast({
          type: "warning",
          message: response.message || "Invalid code. Please try again.",
          timer: 3000,
        });
      } else {
        showToast({
          type: "error",
          message: response?.message || "OTP verification failed.",
          timer: 3000,
        });
      }
      resetOtpInputs();
    } catch (error: any) {
      showToast({
        type: "error",
        message: error.response?.data?.message || "OTP verification failed.",
        timer: 3000,
      });
      resetOtpInputs();
    } finally {
      setIsVerifying(false);
      setIsSigningIn(false);
    }
  };

  return (
    <div className="auth-form-wrapper">
      <AnimatePresence mode="wait">
        {!isRegistered ? (
          /* --- REGISTRATION FORM --- */
          <motion.div
            key="register-form"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="mb-4">
              <h2 className="auth-title">Create Account</h2>
              <p className="auth-subtitle">Sign up for a new experience</p>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Name */}
              <div className="modern-input-group">
                <label className="modern-label">
                  Full Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`modern-input ${errors.name ? "is-invalid" : ""}`}
                  name="name"
                  placeholder="e.g. John Doe"
                  value={form.name}
                  onChange={handleChange}
                  disabled={isSaving}
                />
                {errors.name && (
                  <div className="invalid-feedback d-block mt-1">{errors.name}</div>
                )}
              </div>

              {/* Email */}
              <div className="modern-input-group">
                <label className="modern-label">
                  Email Address <span className="text-danger">*</span>
                </label>
                <input
                  type="email"
                  className={`modern-input ${errors.emailId ? "is-invalid" : ""}`}
                  name="emailId"
                  placeholder="e.g. john@example.com"
                  value={form.emailId}
                  onChange={handleChange}
                  disabled={isSaving}
                />
                {errors.emailId && (
                  <div className="invalid-feedback d-block mt-1">{errors.emailId}</div>
                )}
              </div>

              {/* Mobile */}
              <div className="modern-input-group">
                <label className="modern-label">
                  Mobile Number <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`modern-input ${errors.mobile ? "is-invalid" : ""}`}
                  name="mobile"
                  placeholder="e.g. 9876543210"
                  maxLength={10}
                  value={form.mobile}
                  onChange={handleChange}
                  disabled={isSaving}
                />
                {errors.mobile && (
                  <div className="invalid-feedback d-block mt-1">{errors.mobile}</div>
                )}
              </div>

              {/* Password */}
              <div className="modern-input-group">
                <label className="modern-label">
                  Password <span className="text-danger">*</span>
                </label>
                <div className="input-with-icon">
                  <input
                    type={showPassword ? "text" : "password"}
                    className={`modern-input ${errors.password ? "is-invalid" : ""}`}
                    name="password"
                    placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                    autoComplete="new-password"
                    value={form.password}
                    onChange={handleChange}
                    disabled={isSaving}
                  />
                  <button
                    type="button"
                    className="input-icon-btn"
                    onClick={() => setShowPassword((v) => !v)}
                    tabIndex={-1}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    <i className={`bi ${showPassword ? "bi-eye-slash-fill" : "bi-eye-fill"} fs-5`}></i>
                  </button>
                </div>
                {errors.password && (
                  <div className="invalid-feedback d-block mt-1">{errors.password}</div>
                )}
              </div>

              {/* Confirm Password */}
              <div className="modern-input-group">
                <label className="modern-label">
                  Confirm Password <span className="text-danger">*</span>
                </label>
                <div className="input-with-icon">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    className={`modern-input ${errors.confirmPassword ? "is-invalid" : ""}`}
                    name="confirmPassword"
                    placeholder="Re-enter your password"
                    autoComplete="new-password"
                    value={form.confirmPassword}
                    onChange={handleChange}
                    disabled={isSaving}
                  />
                  <button
                    type="button"
                    className="input-icon-btn"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    tabIndex={-1}
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  >
                    <i className={`bi ${showConfirmPassword ? "bi-eye-slash-fill" : "bi-eye-fill"} fs-5`}></i>
                  </button>
                </div>
                {errors.confirmPassword && (
                  <div className="invalid-feedback d-block mt-1">{errors.confirmPassword}</div>
                )}
              </div>

              <button type="submit" className="modern-btn mt-4" disabled={isSaving}>
                {isSaving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Registering...
                  </>
                ) : (
                  "Create Account"
                )}
              </button>

              <div className="auth-footer justify-content-center mt-3">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onSwitchToLogin();
                  }}
                  className="auth-link"
                >
                  Already have an account? Login here
                </a>
              </div>
            </form>
          </motion.div>
        ) : (
          /* --- OTP VERIFICATION FORM --- */
          <motion.div
            key="otp-form"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="mb-2">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setIsRegistered(false);
                }}
                className="auth-link auth-link-small"
              >
                ← Back to Registration
              </a>
            </div>
            <div className="mb-4 text-center mt-3">
              <h2 className="auth-title">Verify Email</h2>
              <p className="auth-subtitle">
                We've sent a 6-digit code to <br />
                <strong>{form.emailId}</strong>
              </p>
            </div>

            <div className="d-flex flex-column align-items-center">
              <div className="otp-container">
                {otp.map((digit, idx) => (
                  <input
                    key={idx}
                    id={`register-otp-${idx}`}
                    type="text"
                    maxLength={1}
                    className="modern-input otp-input"
                    value={digit}
                    onChange={(e) => handleOtpChange(e.target.value, idx)}
                    onKeyDown={(e) => handleOtpKeyDown(e, idx)}
                    disabled={isVerifying}
                    title={`OTP digit ${idx + 1}`}
                    placeholder="0"
                  />
                ))}
              </div>

              <button
                id="register-verify-btn"
                className="modern-btn mt-4 w-75"
                onClick={handleVerifyOtp}
                disabled={isVerifying}
              >
                {isVerifying ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    {isSigningIn ? "Signing you in..." : "Verifying..."}
                  </>
                ) : (
                  "Verify Code"
                )}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RegisterForm;
